import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Check, Pencil, KeyRound } from 'lucide-react';
import axios from 'axios';
import { TrainerShell } from '../components/TrainerShell';
import { ClientEditDialog } from '../components/ClientEditDialog';
import { SetClientPasswordDialog } from '../components/SetClientPasswordDialog';
import { STAGES, ActionTiles, StageTimeline, StageMetrics } from '../components/ClientCardParts';
import { WorkoutLogFeed } from '../components/WorkoutLogFeed';
import { WeeklyReportsView } from '../components/WeeklyReportsView';
import { PlanReviewPanel } from '../components/PlanReview';
import { SessionBillingPanel } from '../components/SessionBilling';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';


export const ClientPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState(0);
  const [editingNotes, setEditingNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [trainingPlans, setTrainingPlans] = useState([]);
  const [workoutLogs, setWorkoutLogs] = useState([]);
  const [monitoring, setMonitoring] = useState([]);
  const [fmsAssessments, setFmsAssessments] = useState([]);
  const [packages, setPackages] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  useEffect(() => {
    fetchClient();
    fetchTrainingPlansAndLogs();
    fetchCardData();
  }, [id]);

  // Each stage keeps its own note. Re-sync the textarea whenever the active
  // stage (or the underlying client data) changes, so switching tabs never
  // shows — or worse, saves — a different stage's note by accident.
  useEffect(() => {
    if (client) {
      setEditingNotes(client.stages[STAGES[activeStage].key]?.notes || '');
    }
  }, [activeStage, client]);

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients/${id}`, {
        withCredentials: true
      });
      setClient(response.data);
      // Open the card on the first stage that isn't finished yet.
      const firstOpen = STAGES.findIndex((s) => response.data.stages?.[s.key]?.status !== 'completed');
      setActiveStage(firstOpen === -1 ? STAGES.length - 1 : firstOpen);
    } catch (error) {
      console.error('Error fetching client:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainingPlansAndLogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/training-plans/client/${id}`, {
        withCredentials: true
      });
      setTrainingPlans(response.data);

      const logsByPlan = await Promise.all(
        response.data.map(plan =>
          axios.get(`${API_URL}/api/workout-logs/plan/${plan.id}`, { withCredentials: true })
            .then(res => res.data)
            .catch(() => [])
        )
      );
      const allLogs = logsByPlan.flat().sort((a, b) => new Date(b.log_date) - new Date(a.log_date));
      setWorkoutLogs(allLogs);
    } catch (error) {
      console.error('Error fetching training plans:', error);
    }
  };

  // Extras for the card: pain entries, last FMS, active package. Each one is optional.
  const fetchCardData = async () => {
    const get = (url) => axios.get(`${API_URL}${url}`, { withCredentials: true }).then((r) => r.data).catch(() => []);
    const [mon, fms, pkgs] = await Promise.all([
      get(`/api/monitoring/client/${id}?days=60`),
      get(`/api/fms/assessments/client/${id}`),
      get(`/api/packages/client/${id}`),
    ]);
    setMonitoring(mon);
    setFmsAssessments([...fms].sort((x, y) => y.assessment_date.localeCompare(x.assessment_date)));
    setPackages(pkgs);
  };

  const handleStageUpdate = async (stageKey, status) => {
    if (!client || saving) return;
    setSaving(true);
    try {
      const updatedStages = { ...client.stages };
      updatedStages[stageKey] = {
        ...updatedStages[stageKey],
        status,
        notes: editingNotes,
        completed_at: status === 'completed' ? new Date().toISOString() : null
      };

      await axios.put(
        `${API_URL}/api/clients/${id}`,
        { stages: updatedStages },
        { withCredentials: true }
      );

      setClient({ ...client, stages: updatedStages });
    } catch (error) {
      console.error('Error updating stage:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleGenderChange = async (gender) => {
    try {
      await axios.put(`${API_URL}/api/clients/${id}`, { gender }, { withCredentials: true });
      setClient({ ...client, gender });
    } catch (error) {
      console.error('Error saving gender:', error);
    }
  };

  const handleNotesChange = async () => {
    if (!client || saving) return;
    setSaving(true);
    try {
      const stageKey = STAGES[activeStage].key;
      const updatedStages = { ...client.stages };
      updatedStages[stageKey] = {
        ...updatedStages[stageKey],
        notes: editingNotes
      };

      await axios.put(
        `${API_URL}/api/clients/${id}`,
        { stages: updatedStages },
        { withCredentials: true }
      );

      setClient({ ...client, stages: updatedStages });
    } catch (error) {
      console.error('Error saving notes:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !client) {
    return (
      <TrainerShell>
        <div className="min-h-[60vh] flex items-center justify-center text-[var(--text-muted)]">
          {loading ? 'Ładowanie...' : 'Nie znaleziono klienta'}
        </div>
      </TrainerShell>
    );
  }

  const currentStageKey = STAGES[activeStage].key;
  const currentStage = client.stages[currentStageKey];
  const activePlan = trainingPlans.find((p) => p.status === 'active') || null;
  const pkg = packages.filter((p) => p.status === 'active').sort((a, b) => b.purchased_date.localeCompare(a.purchased_date))[0] || null;
  const fms = fmsAssessments[0] || null;
  const lastEntry = [...monitoring].sort((a, b) => b.log_date.localeCompare(a.log_date))[0] || null;
  const doneCount = STAGES.filter((s) => client.stages[s.key]?.status === 'completed').length;
  const initials = client.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const ringPct = Math.round((doneCount / 5) * 100);

  return (
    <TrainerShell>
      <main className="max-w-[1320px] px-4 sm:px-10 py-6 sm:py-10 flex flex-col gap-5 sm:gap-6" data-testid="client-page">
        <button onClick={() => navigate('/dashboard')} className="chip w-fit text-[var(--text-muted)]" data-testid="back-to-dashboard-button">
          <ArrowLeft className="w-3.5 h-3.5" /> Klienci
        </button>

        <section className="glass rounded-[28px] p-5 sm:p-8 flex flex-wrap items-center justify-between gap-5">
          <div className="flex items-center gap-4 sm:gap-6 min-w-0">
            <div className="w-[76px] h-[76px] sm:w-24 sm:h-24 rounded-full p-[3px] shrink-0"
              style={{ background: `conic-gradient(#C9A84C 0 ${ringPct}%, var(--track) ${ringPct}% 100%)` }}>
              <div className="w-full h-full rounded-full bg-[var(--bg-primary)] flex items-center justify-center text-2xl font-extrabold">{initials}</div>
            </div>
            <div className="min-w-0 flex flex-col gap-3">
              <h1 className="big-num text-3xl sm:text-5xl break-words" data-testid="client-name">{client.name}</h1>
              <div className="flex flex-wrap gap-2 items-center">
                {client.age != null && <span className="chip">{client.age} lat</span>}
                {client.segment && <span className="chip">Segment {client.segment}</span>}
                {client.session_price != null && <span className="chip">{client.session_price} zł / trening</span>}
                <span className="chip border-[#C9A84C]/50">Etap {Math.min(5, doneCount + 1)} / 5</span>
                <Select value={client.gender || ''} onValueChange={handleGenderChange}>
                  <SelectTrigger className="chip h-auto w-auto gap-2 text-xs" data-testid="client-gender-select">
                    <SelectValue placeholder="Płeć (dla mapy ciała)" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--bg-card)] border-[var(--border-soft)] text-[var(--text-primary)]">
                    <SelectItem value="male">Mężczyzna</SelectItem>
                    <SelectItem value="female">Kobieta</SelectItem>
                  </SelectContent>
                </Select>
                <button type="button" onClick={() => setEditOpen(true)} className="chip" data-testid="edit-client-button">
                  <Pencil className="w-3.5 h-3.5" /> Dane klienta
                </button>
                {client.email && (
                  <button type="button" onClick={() => setPasswordOpen(true)} className="chip" data-testid="set-client-password-button">
                    <KeyRound className="w-3.5 h-3.5" /> Ustaw hasło
                  </button>
                )}
                {client.email
                  ? <span className="hidden md:inline text-sm text-[var(--text-muted)] ml-1">{client.email}</span>
                  : <span className="text-xs text-[var(--text-muted)] ml-1">bez konta (brak e-maila)</span>}
              </div>
            </div>
          </div>
        </section>

        <PlanReviewPanel clientId={id} navigate={navigate} />

        <SessionBillingPanel key={client.session_price ?? ''} clientId={id} />

        <ActionTiles id={id} navigate={navigate} client={client} fms={fms} activePlan={activePlan} lastEntry={lastEntry} pkg={pkg} />

        <div className="grid lg:grid-cols-[minmax(300px,400px)_1fr] gap-5 sm:gap-6 items-start">
          <section className="glass rounded-[28px] p-6 sm:p-7">
            <p className="label-text text-[var(--text-muted)] mb-2.5">Metoda</p>
            <h2 className="text-xl font-extrabold tracking-tight mb-6" data-testid="system-heading">System Staniszewskiego™</h2>
            <StageTimeline client={client} activeStage={activeStage} setActiveStage={setActiveStage} />
          </section>

          <section className="glass rounded-[28px] p-5 sm:p-9 flex flex-col gap-6 min-w-0">
            <div className="flex justify-between items-start flex-wrap gap-4">
              <div className="flex flex-col gap-2.5">
                <p className={`label-text ${currentStage?.status === 'completed' ? 'text-[var(--text-muted)]' : 'text-[#C9A84C]'}`}>
                  Etap {activeStage + 1} z 5 · {currentStage?.status === 'completed'
                    ? `ukończony ${new Date(currentStage.completed_at).toLocaleDateString('pl-PL')}` : 'w toku'}
                </p>
                <h2 className="big-num text-3xl sm:text-4xl" data-testid="active-stage-title">{STAGES[activeStage].label}</h2>
                <p className="text-[var(--text-muted)] font-light">{STAGES[activeStage].description}</p>
              </div>
              {currentStage?.status === 'completed' ? (
                <button onClick={() => handleStageUpdate(currentStageKey, 'pending')} disabled={saving}
                  className="cta-ghost h-12 px-6 rounded-full text-xs font-semibold tracking-[0.2em] uppercase" data-testid="mark-incomplete-button">
                  {saving ? 'Zapisywanie...' : 'Cofnij ukończenie'}
                </button>
              ) : (
                <button onClick={() => handleStageUpdate(currentStageKey, 'completed')} disabled={saving}
                  className="cta-primary h-12 px-6 rounded-full text-xs font-bold tracking-[0.2em] uppercase flex items-center gap-2" data-testid="mark-complete-button">
                  <Check className="w-4 h-4" /> {saving ? 'Zapisywanie...' : 'Zakończ etap'}
                </button>
              )}
            </div>

            <StageMetrics monitoring={monitoring} fms={fms} workoutLogs={workoutLogs} />

            <div>
              <label className="label-text text-[var(--text-muted)] mb-3 block">Notatki do etapu</label>
              <Textarea
                key={currentStageKey}
                value={editingNotes}
                onChange={(e) => setEditingNotes(e.target.value)}
                onBlur={handleNotesChange}
                placeholder="Dodaj notatki dla tego etapu..."
                className="bg-[var(--glass-soft)] border-[var(--glass-soft-border)] rounded-[20px] text-[var(--text-primary)] min-h-[140px] font-light"
                data-testid="stage-notes-textarea"
              />
            </div>

            {(currentStageKey === 'plan' || currentStageKey === 'utrzymanie_efektow') && (
              <div className="flex flex-col gap-2.5">
                <label className="label-text text-[var(--text-muted)]">Plany treningowe</label>
                {trainingPlans.length === 0 && <p className="text-sm text-[var(--text-muted)]">Klient nie ma jeszcze planu treningowego.</p>}
                {trainingPlans.map((plan) => (
                  <button key={plan.id} onClick={() => navigate(`/client/${id}/plan-builder/${plan.id}`)}
                    className="glass-soft rounded-2xl p-4 flex items-center justify-between text-left hover:border-[#C9A84C]/50" data-testid={`linked-plan-${plan.id}`}>
                    <span>
                      <span className="block font-semibold">{plan.plan_name}</span>
                      <span className="block text-xs text-[var(--text-muted)] mt-0.5">{plan.frequency} dni/tydzień · {plan.status} · {plan.exercises.length} ćwiczeń</span>
                    </span>
                    <span className="text-xs text-[var(--text-muted)] shrink-0 ml-4">Edytuj →</span>
                  </button>
                ))}
                <div className="flex flex-wrap gap-2 mt-1">
                  <button onClick={() => navigate(`/client/${id}/plan-builder`)} className="chip" data-testid="utrzymanie-plan-builder-link">+ Nowy plan</button>
                  <button onClick={() => navigate(`/client/${id}/plan-builder/import`)} className="chip" data-testid="excel-import-link">Importuj z Excela</button>
                </div>
              </div>
            )}

            <WeeklyReportsView clientId={id} />

            <div className="flex flex-col gap-2.5">
              <label className="label-text text-[var(--text-muted)]">Wykonane treningi</label>
              <WorkoutLogFeed logs={workoutLogs} onUpdated={(u) => setWorkoutLogs((all) => all.map((l) => (l.id === u.id ? u : l)))} />
            </div>
          </section>
        </div>
      </main>
      <ClientEditDialog client={client} open={editOpen} onOpenChange={setEditOpen} onSaved={setClient} />
      <SetClientPasswordDialog client={client} open={passwordOpen} onOpenChange={setPasswordOpen} />
    </TrainerShell>
  );
};
